import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import dayjs from "dayjs";
import {EventComment, EventItem, EventSearchParams} from '@communication/_common/models/events.models';
import * as EventsActions from "@communication/events/_store/events.actions";
import {DahLoadingType} from '@common/components-module/core-components.models';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const eventsFeatureKey = 'events';

export function sortByDate(a: EventItem, b: EventItem) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'created');
}

export const eventItemsAdapter: EntityAdapter<EventItem> = createEntityAdapter<EventItem>({
    sortComparer: sortByDate
});

export function sortCommentsByDate(a: EventComment<any>, b: EventComment<any>) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'createTime');
}

export const commentAdapter: EntityAdapter<EventComment<any>> = createEntityAdapter<EventComment<any>>({
    sortComparer: sortCommentsByDate
});

export interface EventsState {
    list: EntityStateModel<EventItem, EventSearchParams>;
    openedItem: {
        main?: EventItem,
        comments: EntityStateModel<EventComment<any>, any>
    }
}

export const eventsInitialsState: EventsState = {
    list: eventItemsAdapter.getInitialState({
        listLoading: 'listLoading',
        pagination: {size: 5},
        itemsLoading: []
    }),
    openedItem: {
        comments: commentAdapter.getInitialState({
            listLoading: 'listLoading',
            pagination: {},
            itemsLoading: []
        }),
    }
};


export const eventsReducer = createReducer(
    eventsInitialsState,

    // <editor-fold desc="Events list">
    on(EventsActions.LoadList, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.list.ids)
        },
    })),

    on(EventsActions.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            list: action.payload.clearState
                ? eventItemsAdapter.setAll(action.payload.items, {
                    ...state.list,
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : eventItemsAdapter.upsertMany(action.payload.items, {
                    ...state.list,
                    pagination: action.payload.pagination,
                    listLoading: undefined
                })
        });
    }),

    on(EventsActions.LoadListFailure, (state) => ({
        ...state,
        list: {
            ...state.list,
            loading: undefined
        },
    })),

    on(EventsActions.SetFilter, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            filter: action.payload
        }
    })),

    on(EventsActions.SetPagination, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            pagination: {...state.list.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.list.pagination, state.list.ids?.length) : 0}
        }
    })),

    on(EventsActions.SetLoading, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            listLoading: action.payload
        }
    })),

    on(EventsActions.SetItemsLoading, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    // </editor-fold desc="Events list">

    // <editor-fold desc="Event item">

    on(EventsActions.SetOpenedItem, (state, action) => ({
        ...state,
        openedItem: {
            main: action.payload,
            comments: commentAdapter.setAll(action.payload?.lastComment ? [action.payload.lastComment] : [], {
                ...state.openedItem.comments
            })
        },
    })),

    on(EventsActions.ClearOpenedItem, (state) => ({
        ...state,
        openedItem: eventsInitialsState.openedItem
    })),

    on(EventsActions.UpdateOpenedItem, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            main: action.payload
        }
    })),

    on(EventsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.list.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return ({
                    ...state,
                    list: eventItemsAdapter.setOne(action.payload!, {
                        ...state.list,
                        itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.id!, false),
                        pagination: {
                            ...state.list.pagination,
                            totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.list.pagination, state?.list?.ids)
                        }
                    })
                }
            );
        }

        return ({
                ...state,
                list: eventItemsAdapter.removeOne(action.payload.id!, {
                    ...state.list,
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.id!, false),
                    pagination: {
                        ...state.list.pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.list.pagination, state?.list?.ids, true)
                    }
                })
            }
        );
    }),

    on(EventsActions.DeleteItem, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.id!, true)
        }
    })),

    on(EventsActions.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            list: eventItemsAdapter.removeOne(action.payload.id!, {
                ...state.list,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.list.itemsLoading, action.payload.id!, false),
                pagination: {
                    ...state.list.pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.list.pagination, state?.list?.ids, true)
                }
            })
        });
    }),

    // </editor-fold desc="Event item">

    // <editor-fold desc="Event comments">

    on(EventsActions.LoadComments, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: 'listLoading' as DahLoadingType,
            }
        },
    })),

    on(EventsActions.LoadCommentsSuccess, (state, action) => {
        return {
            ...state,
            openedItem: {
                ...state.openedItem,
                comments: commentAdapter.upsertMany(action.payload.comments, {
                    ...state.openedItem.comments,
                    pagination: action.payload.pagination,
                    listLoading: undefined,
                }),
            },
        };
    }),

    on(EventsActions.LoadCommentsFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: undefined,
            }
        },
    })),

    on(EventsActions.SendComment, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: 'entityUpdating' as DahLoadingType,
            }
        }
    })),

    on(EventsActions.SendCommentSuccess, (state, action) => {
        const updatedMain = state.openedItem.main
            ? {...state.openedItem.main, commentsCount: action.payload.commentsCount}
            : undefined;

        return ({
            ...state,
            openedItem: {
                ...state.openedItem,
                main: updatedMain,
                comments: commentAdapter.addOne(action.payload, {
                    ...state.openedItem.comments,
                    listLoading: undefined
                })
            }
        });
    }),

    on(EventsActions.SendCommentFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: undefined
            }
        }
    })),

    on(EventsActions.UpdateCommentSuccess, (state, action) => {
        return ({
            ...state,
            openedItem: {
                ...state.openedItem,
                comments: commentAdapter.updateOne({id: action.payload.id!, changes: action.payload}, {
                    ...state.openedItem.comments,
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.id!, false),
                })
            }
        });
    }),

    on(EventsActions.DeleteComment, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.comment.id!, true),
            }
        }
    })),

    on(EventsActions.DeleteCommentSuccess, (state, action) => {
        const updatedMain = state.openedItem.main
            ? {...state.openedItem.main, commentsCount: state.openedItem.main.commentsCount ? state.openedItem.main.commentsCount - 1 : 0}
            : undefined;

        return ({
            ...state,
            openedItem: {
                ...state.openedItem,
                main: updatedMain,
                comments: commentAdapter.removeOne(action.payload.id!, {
                    ...state.openedItem.comments,
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.id!, false),
                })
            }
        });
    }),

    on(EventsActions.SetCommentItemsLoading, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.id!, true),
            }
        }
    })),

    // </editor-fold desc="Event comments">

    on(EventsActions.SetInitialState, SetAllInitialStates, () => ({...eventsInitialsState})),
);

export const {selectAll: selectAllEntities} = eventItemsAdapter.getSelectors();
export const {selectAll: selectAllCommentsEntities} = commentAdapter.getSelectors();


const isAvailableByFilter = (searchParams: EventSearchParams | undefined, item: EventItem): boolean => {

    if (!searchParams) {
        return true;
    }

    if (searchParams.query && item.title && item.title.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1) {
        return false;
    }

    if (searchParams.types && !searchParams.types.includes(item.type)) {
        return false;
    }

    if (searchParams.group?.id && searchParams.group.id != item.group?.id) {
        return false;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(item.created))) {
        return false;
    }

    return !(searchParams.to && dayjs(item.created).isAfter(dayjs(searchParams.to)));
};
